
import { ThemeProvider } from "styled-components";
import * as Sentry from "@sentry/react";
import { SetClientID } from "@zerolight-core/zl-auth-handler";
import { ZLBoltInstance, ZLBoltOptions } from "@zerolight-core/libzl";
import { LibZLInstanceProvider, ZLBoltInstanceProvider, Direct2DProvider} from "@zerolight-core/libzl-react";
import { Theme, GlobalStyle } from "slate-ui-components";
import { Configurator } from "./Configurator";
import { BuildSessionProvider, useBuildSession } from "./BuildSession";
import { AnalyticsProvider } from "./AnalyticsProvider";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { updateBuildSession, VisualizerSlice } from "./store/reducers/visualizer";
import { requireToken, setupAuth } from "./utils/auth";
import { AppConfig } from "./config";
import { useEffect, useMemo } from "react";
import React from "react";
import { useAppSelector } from "./store/hooks";
import { getInitialUrlArgs } from "./UrlArgsProvider";
import { Direct2D } from "./components/Direct2D";
import { Preload } from "./Preload";
import {FrameStateProvider} from "./FrameStateProvider";


if(window.location.hostname.includes("-dev.slate.zlthunder.net") || window.location.hostname.includes("-test.slate.zlthunder.net") || window.location.hostname.includes("-prelive.slate.zlthunder.net")) {
  Sentry.init({
    dsn: "https://e3d4419d506a46b8899eb07215ac3f72@sentry.zlthunder.net/21",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: window.location.hostname.includes("-live.slate.zlthunder.net") ? 0.01 : 1.0,
    environment: location.hostname.split(".")[0],
  });
}

const {assetService} = getInitialUrlArgs()

SetClientID("SlateConfigurator");

if(requireToken(assetService)) {
  setupAuth({
    clientID: "SlateConfigurator",
    scope: [`renderingservice.omnistream.${assetService}.access`]
  })
}

const configureZLBoltGlobals = (zlbolt: ZLBoltInstance) => {
  zlbolt.movableExteriorCamera = "Orbit";
};

export const App = () => {
  return (
    <>
      <GlobalStyle />
      <ThemeProvider theme={Theme}>
        <LibZLInstanceProvider>
          <ZLBoltInstanceProvider
            projectName="configurator"
            configure={configureZLBoltGlobals}
          >
            <AnalyticsProvider>
              <BuildSessionProvider>
                <ZLStore>
                  <D2DProvider>
                    <FrameStateProvider>
                       <Configurator/>
                    </FrameStateProvider>
                  </D2DProvider>
                </ZLStore>
              </BuildSessionProvider>
            </AnalyticsProvider>
          </ZLBoltInstanceProvider>
        </LibZLInstanceProvider>
      </ThemeProvider>
    </>
  )
}

const ZLStore: React.FC<React.PropsWithChildren> = ({ children }) => {
  const buildSessionContext = useBuildSession()
  const { current: build, initialConfigData } = buildSessionContext

  const storeInstance = useMemo(() =>
  {
    const s = store();
    s.dispatch(VisualizerSlice.actions.updateConfig(AppConfig));
    return s;
  }, []);

  useEffect(() => {
    if(initialConfigData?.customWraps) {
      storeInstance.dispatch(VisualizerSlice.actions.deselectAllCustomWraps())
      storeInstance.dispatch(VisualizerSlice.actions.removeCustomWrap())
      for(const [id, wrap] of Object.entries(initialConfigData.customWraps)) {
        storeInstance.dispatch(VisualizerSlice.actions.addCustomWrap({id, wrap}))
      }
    }
  }, [initialConfigData, storeInstance])
  
  useEffect(() => { setTimeout(() => build && storeInstance.dispatch(updateBuildSession(build)), 0) }, [build, storeInstance]);

  return (
    <Provider store={storeInstance}>
      <Direct2D>
        {children}
        <Preload/>
      </Direct2D>
    </Provider>
  );
};

const D2DProvider : React.FC<React.PropsWithChildren> = ({children}) => {
  const zlboltOptions = useAppSelector(s => "zlboltOptions" in s.configuration.currentVisualizerConfig ? s.configuration.currentVisualizerConfig.zlboltOptions : undefined as never);
  const d2dOptions = useMemo(() => JSON.parse(JSON.stringify(zlboltOptions)) as ZLBoltOptions, [zlboltOptions])
  return (
    <Direct2DProvider options={d2dOptions}>
      {children}
    </Direct2DProvider>
  )
}


export default App;
